import { useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import {
  Col,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap'

import CustomButton from 'components/Buttons/CustomButton'
import AmountInput from 'components/AmountInput'
import PMBInputNumber from 'components/PMBInputNumber'
import { splitToManyInventory, mergeInventory } from 'features/public-views/manageStock/API/manageStockAction'
import { getInventories } from '../sellCommodityAction'

/**
 * ModalAutoPick - Component untuk auto-pick inventori berdasarkan berat yang dibutuhkan
 *
 * @param {boolean} isOpen - Status modal terbuka atau tidak
 * @param {function} toggle - Function untuk toggle modal
 * @param {object} orderItem - Item order yang sedang di-pick (orders.item[idx])
 * @param {array} inventories - List inventori yang tersedia
 * @param {array} packings - List packing/grup inventori
 * @param {object} autoFetchPublicVersion - Data unit array dari hook
 * @param {function} onApply - Callback ketika auto-pick berhasil diterapkan
 * @param {number} fieldNum - Field number untuk tracking
 * @param {array} invPacking - Inventory packing data
 */
const ModalAutoPick = ({
  isOpen,
  toggle,
  orderItem,
  inventories,
  packings,
  autoFetchPublicVersion,
  onApply,
  fieldNum,
  invPacking,
}) => {
  const dispatch = useDispatch()
  const intl = useIntl()

  // Modal state
  const [loading, setLoading] = useState(false)
  const [modalNeededKg, setModalNeededKg] = useState('')
  // Keep a unit value to use in labels and payload, default to 'Kg' fallback
  const [modalUnit, setModalUnit] = useState('Kg')
  const [modalPcs, setModalPcs] = useState('1')
  const [modalGenRows, setModalGenRows] = useState([])
  const [modalGenError, setModalGenError] = useState('')
  const [modalHasGenerated, setModalHasGenerated] = useState(false)
  const [modalSplitPlans, setModalSplitPlans] = useState([])
  const [modalSelectedRows, setModalSelectedRows] = useState([])
  const [modalAvailableKg, setModalAvailableKg] = useState(0)
  const [modalAvailableCount, setModalAvailableCount] = useState(0)
  const [modalUsedInventoryCount, setModalUsedInventoryCount] = useState(0)
  const [modalMergePlan, setModalMergePlan] = useState(null)

  // Helper: Parse number dari format Indonesia (1.234,56)
  const parseNumber = (val) => {
    if (val === undefined || val === null) return 0
    if (typeof val === 'number') return val
    try {
      return (
        Number(
          val?.replace?.(/\./g, '')?.replace?.(/\,/g, '.')?.slice?.(0, 15),
        ) || 0
      )
    } catch (e) {
      return 0
    }
  }

  // Helper to round numeric values for consistent sums and to avoid float errors
  const roundTo = (n, decimals = 6) => {
    const num = typeof n === 'number' ? n : parseNumber(n)
    return Number(Number(num).toFixed(decimals))
  }

  // Helper: Resolve preferred packing/lot ID
  const resolvePreferredPackingId = () => {
    if (!orderItem) return null

    const candidatePackingId =
      orderItem?.packingId ??
      orderItem?.groupId ??
      orderItem?.lotId ??
      orderItem?.inventoryGroupId ??
      null

    if (candidatePackingId) return candidatePackingId

    const existingPacking = invPacking?.find(
      (item) => item?.fieldNum === fieldNum && item?.packingId,
    )
    return existingPacking?.packingId ?? null
  }

  // Reset semua state modal
  const resetModalStates = () => {
    setModalGenRows([])
    setModalGenError('')
    setModalHasGenerated(false)
    setModalNeededKg('')
    setModalPcs('1')
    setModalSplitPlans([])
    setModalSelectedRows([])
    setModalAvailableKg(0)
    setModalAvailableCount(0)
    setModalUsedInventoryCount(0)
    setModalMergePlan(null)
    setLoading(false)
  }

  // Initialize modal dengan data dari orderItem
  const initializeModal = () => {
    setModalGenError('')
    setModalGenRows([])
    setModalHasGenerated(false)
    // Set initial value as string, default to empty string if no amount
    const initialAmount = orderItem?.amount
    setModalNeededKg(initialAmount ? String(initialAmount) : '')
    setModalUnit(
      (orderItem?.unit?.value || orderItem?.unit || 'Kg')?.toString() ?? 'Kg',
    )
    setModalPcs('1')
  }

  // Generate picking rows berdasarkan input
  const generateAutoPickFromModal = async () => {
    try {
      setModalGenError('')
      setModalGenRows([])
      setModalHasGenerated(false)
      setModalAvailableKg(0)
      setModalAvailableCount(0)
      setModalUsedInventoryCount(0)
      setLoading(true)

      const needed = parseNumber(modalNeededKg)
      if (!needed || needed <= 0) {
        setModalGenError('Masukkan berat yang dibutuhkan terlebih dahulu.')
        setLoading(false)
        setModalHasGenerated(true)
        return
      }

      // Resolve item ID
      let itemId = orderItem?.itemId ?? null
      if (!itemId && orderItem?.id) {
        const invMatch = (inventories ?? []).find(
          (inv) => Number(inv?.id) === Number(orderItem?.id),
        )
        if (invMatch) itemId = invMatch.itemId ?? invMatch.item?.id
        if (!itemId) {
          const packingMatch = (packings ?? []).find(
            (p) => Number(p?.id) === Number(orderItem?.id),
          )
          if (packingMatch)
            itemId = packingMatch?.itemId ?? packingMatch?.item?.id
        }
      }
      itemId = itemId ?? orderItem?.id
      if (!itemId) {
        setModalGenError('Pilih barang terlebih dahulu.')
        setLoading(false)
        setModalHasGenerated(true)
        return
      }

      // Fetch inventories jika belum ada
      let sourceInventories = inventories ?? []
      if (!Array.isArray(sourceInventories) || sourceInventories.length === 0) {
        try {
          sourceInventories = await dispatch(getInventories()).unwrap()
        } catch (err) {
          sourceInventories = inventories ?? []
        }
      }

      // Filter inventori yang cocok
      const targetName = String(orderItem?.nameItem ?? orderItem?.label ?? '')
        .trim()
        .toLowerCase()
      const targetUnit = String(
        orderItem?.unit?.value ??
          orderItem?.unit?.label ??
          orderItem?.unit ??
          modalUnit,
      )
        .trim()
        .toLowerCase()
      const targetLot = orderItem?.lotId ?? null

      const available = (sourceInventories ?? []).filter((e) => {
        if (e?.groupId !== null && e?.groupId !== undefined) return false

        const eName = String(e?.nameItem ?? e?.item?.nameItem ?? '')
          .trim()
          .toLowerCase()
        const eUnit = String(e?.unit ?? e?.item?.unit ?? '')
          .trim()
          .toLowerCase()
        const eLot = e?.lotId ?? null

        if (Number(e?.itemId) !== Number(itemId)) return false
        if (targetLot && String(eLot ?? '') !== String(targetLot ?? ''))
          return false
        if (
          targetName &&
          eName !== targetName &&
          !eName.includes(targetName) &&
          !targetName.includes(eName)
        ) {
          return false
        }
        if (eUnit !== targetUnit) return false

        return true
      })

      // Determine preferred packing (lot/ group) and filter available accordingly before allocation
      const preferredPackingIdForMapping = resolvePreferredPackingId()
      const shouldFilterByLot =
        preferredPackingIdForMapping !== null &&
        preferredPackingIdForMapping !== undefined
      const normalizedLotId = shouldFilterByLot
        ? Number(preferredPackingIdForMapping)
        : null

      const availableToUse = shouldFilterByLot
        ? (available || []).filter(
            (e) => Number(e.packingId) === normalizedLotId || !e.packingId,
          )
        : available

      // We'll later possibly filter by preferred packing (lot) - so compute
      // both total available and the filtered available
      const availableKg = (availableToUse || []).reduce(
        (sum, row) =>
          sum +
          parseNumber(row.amount ?? row.item?.amount ?? row?.item?.size ?? 0),
        0,
      )
      setModalAvailableKg(availableKg)
      setModalAvailableCount(availableToUse?.length ?? 0)

      if (!available || available.length === 0) {
        setModalGenError('Tidak ada inventori yang cocok untuk barang ini.')
        setLoading(false)
        setModalHasGenerated(true)
        return
      }

      // Sort descending by amount
      let sorted = [...availableToUse]
        .map((s) => ({
          ...s,
          amountNum: parseNumber(s.amount ?? s.item?.amount ?? s?.item?.size ?? 0),
        }))
        .filter((s) => s.amountNum > 0)
        .sort((a, b) => b.amountNum - a.amountNum)

      const requiredPcsNum = Math.max(1, Number(modalPcs) || 1)
      const p = requiredPcsNum

      const TOLERANCE = 0.001

      // 1. Check if total available is enough
      const totalAvailable = sorted.reduce((sum, inv) => sum + inv.amountNum, 0)
      if (needed > totalAvailable + TOLERANCE) {
        setModalGenError(
          `Stok tidak mencukupi. Tersedia: ${intl.formatNumber(totalAvailable)} ${modalUnit}, diminta: ${intl.formatNumber(needed)} ${modalUnit}.`
        )
        setLoading(false)
        setModalHasGenerated(true)
        return
      }

      let selectedRows = []
      const splitMap = new Map()
      let mergePlanLocal = null

      // Check if we need to merge
      let k = -1
      const maxKSearch = Math.min(p - 1, sorted.length - 1)
      for (let i = maxKSearch; i >= 0; i--) {
        let sumI = 0
        for (let j = 0; j < i; j++) {
          sumI += sorted[j].amountNum
        }
        const remWeight = needed - sumI
        const numPiecesToSplit = p - i
        const splitInv = sorted[i]

        const isWeightWithinCapacity = remWeight <= splitInv.amountNum + TOLERANCE
        const isWeightPositive = remWeight >= 0

        let isIntegerSplitValid = true
        if (Number.isInteger(needed) && needed >= p) {
          isIntegerSplitValid = remWeight >= numPiecesToSplit
        }

        if (isWeightPositive && isWeightWithinCapacity && isIntegerSplitValid) {
          k = i
          break
        }
      }

      const needsMerge = (k === -1)

      if (!needsMerge) {
        // CASE A: No merge needed.
        let sumK = 0
        for (let i = 0; i < k; i++) {
          sumK += sorted[i].amountNum
        }

        const intactInvs = sorted.slice(0, k)
        const remWeight = needed - sumK

        // The remaining p - k pieces must come from the (k+1)-th inventory (which is sorted[k])
        const splitInv = sorted[k]
        const numPiecesToSplit = p - k

        // We split splitInv into numPiecesToSplit pieces
        const splitAmountPerPiece = remWeight / numPiecesToSplit

        // If in integer mode, try to distribute as integers round-robin
        let pieceWeights = []
        const isIntegerModePossible =
          Number.isInteger(needed) &&
          Number.isInteger(splitAmountPerPiece * numPiecesToSplit) &&
          (Math.floor(splitAmountPerPiece) > 0 || remWeight >= numPiecesToSplit)

        if (isIntegerModePossible) {
          const baseWeight = Math.floor(splitAmountPerPiece)
          pieceWeights = Array(numPiecesToSplit).fill(baseWeight)
          const currentSum = pieceWeights.reduce((s, w) => s + w, 0)
          let diff = Math.round(remWeight - currentSum)
          let idx = 0
          while (diff > 0) {
            pieceWeights[idx] += 1
            diff -= 1
            idx = (idx + 1) % numPiecesToSplit
          }
        } else {
          pieceWeights = Array(numPiecesToSplit).fill(roundTo(splitAmountPerPiece))
          const currentSum = roundTo(pieceWeights.reduce((s, w) => s + w, 0))
          const diff = roundTo(remWeight - currentSum)
          if (Math.abs(diff) > 0) {
            pieceWeights[pieceWeights.length - 1] = roundTo(pieceWeights[pieceWeights.length - 1] + diff)
          }
        }

        // Add intact inventories to selectedRows
        intactInvs.forEach((inv, idx) => {
          selectedRows.push({
            src: 'direct',
            originalInventory: inv,
            origInventoryId: inv.id,
            amount: inv.amountNum,
            pcs: 1,
            pieceIndex: idx,
          })
        })

        // Prepare the split plan for splitInv
        const isSplitNeeded = numPiecesToSplit > 1 || roundTo(remWeight) < roundTo(splitInv.amountNum) - TOLERANCE
        if (isSplitNeeded) {
          const arrNew = pieceWeights.map((w) => ({
            amount: w,
            unit: splitInv.unit?.value || splitInv.unit || splitInv.item?.unit || modalUnit,
            itemId: splitInv.itemId ?? splitInv.item?.itemId,
          }))

          const usedSum = pieceWeights.reduce((s, w) => s + w, 0)
          const remainder = roundTo(splitInv.amountNum - usedSum)
          if (remainder > TOLERANCE) {
            arrNew.push({
              amount: remainder,
              unit: splitInv.unit?.value || splitInv.unit || splitInv.item?.unit || modalUnit,
              itemId: splitInv.itemId ?? splitInv.item?.itemId,
            })
          }

          splitMap.set(splitInv.id, {
            inventoryId: splitInv.id,
            arrNew,
            originalInventory: splitInv,
          })

          pieceWeights.forEach((w, pieceIdx) => {
            selectedRows.push({
              src: 'plannedSplit',
              originalInventory: splitInv,
              origInventoryId: splitInv.id,
              amount: w,
              pcs: 1,
              splitPlanPendingId: splitInv.id,
              splitArrIndex: pieceIdx,
              pieceIndex: k + pieceIdx,
            })
          })
        } else {
          // No split actually needed, take splitInv whole
          selectedRows.push({
            src: 'direct',
            originalInventory: splitInv,
            origInventoryId: splitInv.id,
            amount: splitInv.amountNum,
            pcs: 1,
            pieceIndex: k,
          })
        }

      } else {
        // CASE B: Merge needed.
        // Keep the top p - 1 inventories whole (intact), capped by sorted.length - 2
        let numIntact = Math.min(p - 1, Math.max(0, sorted.length - 2))
        let sumK = 0
        for (let i = 0; i < numIntact; i++) {
          sumK += sorted[i]?.amountNum || 0
        }

        // If target is integer, and the remaining weight is smaller than the remaining pieces to split,
        // we must reduce the number of intact inventories so we have more weight to split.
        if (Number.isInteger(needed) && needed >= p) {
          while (numIntact > 0 && (needed - sumK) < (p - numIntact)) {
            numIntact--
            sumK = 0
            for (let i = 0; i < numIntact; i++) {
              sumK += sorted[i]?.amountNum || 0
            }
          }
        }

        const intactInvs = sorted.slice(0, numIntact)
        const remWeight = needed - sumK

        // Merge the minimum necessary smaller inventories to get at least remWeight
        const remainingInvs = sorted.slice(numIntact)
        const mergeInvs = []
        let mergeSum = 0
        for (let i = 0; i < remainingInvs.length; i++) {
          mergeInvs.push(remainingInvs[i])
          mergeSum += remainingInvs[i].amountNum
          if (mergeSum >= remWeight - TOLERANCE) {
            break
          }
        }

        const firstMergeInv = mergeInvs[0]
        mergePlanLocal = {
          arrInventoryIds: mergeInvs.map((inv) => inv.id),
          newAmount: mergeSum,
          unit: firstMergeInv.unit?.value || firstMergeInv.unit || modalUnit,
          newItemId: firstMergeInv.itemId ?? firstMergeInv.item?.itemId,
          ownerCode: firstMergeInv.ownerCode ?? null,
        }

        const mergedVirtual = {
          id: 'temp-merged',
          amountNum: mergeSum,
          itemId: firstMergeInv.itemId ?? firstMergeInv.item?.itemId,
          unit: firstMergeInv.unit,
          ownerCode: firstMergeInv.ownerCode,
          nameItem: firstMergeInv.nameItem,
          isVirtualMerged: true,
        }

        // Add intact inventories to selectedRows
        intactInvs.forEach((inv, idx) => {
          selectedRows.push({
            src: 'direct',
            originalInventory: inv,
            origInventoryId: inv.id,
            amount: inv.amountNum,
            pcs: 1,
            pieceIndex: idx,
          })
        })

        // The remaining piece must come from the merged virtual inventory
        const numPiecesToSplit = p - numIntact
        const isSplitNeeded = numPiecesToSplit > 1 || roundTo(remWeight) < roundTo(mergeSum) - TOLERANCE
        if (isSplitNeeded) {
          const splitAmountPerPiece = remWeight / numPiecesToSplit

          // If in integer mode, try to distribute as integers round-robin
          let pieceWeights = []
          const isIntegerModePossible =
            Number.isInteger(needed) &&
            Number.isInteger(splitAmountPerPiece * numPiecesToSplit) &&
            (Math.floor(splitAmountPerPiece) > 0 || remWeight >= numPiecesToSplit)

          if (isIntegerModePossible) {
            const baseWeight = Math.floor(splitAmountPerPiece)
            pieceWeights = Array(numPiecesToSplit).fill(baseWeight)
            const currentSum = pieceWeights.reduce((s, w) => s + w, 0)
            let diff = Math.round(remWeight - currentSum)
            let idx = 0
            while (diff > 0) {
              pieceWeights[idx] += 1
              diff -= 1
              idx = (idx + 1) % numPiecesToSplit
            }
          } else {
            pieceWeights = Array(numPiecesToSplit).fill(roundTo(splitAmountPerPiece))
            const currentSum = roundTo(pieceWeights.reduce((s, w) => s + w, 0))
            const diff = roundTo(remWeight - currentSum)
            if (Math.abs(diff) > 0) {
              pieceWeights[pieceWeights.length - 1] = roundTo(pieceWeights[pieceWeights.length - 1] + diff)
            }
          }

          const arrNew = pieceWeights.map((w) => ({
            amount: w,
            unit: firstMergeInv.unit?.value || firstMergeInv.unit || modalUnit,
            itemId: firstMergeInv.itemId ?? firstMergeInv.item?.itemId,
          }))

          const usedSum = pieceWeights.reduce((s, w) => s + w, 0)
          const remainder = roundTo(mergeSum - usedSum)
          if (remainder > TOLERANCE) {
            arrNew.push({
              amount: remainder,
              unit: firstMergeInv.unit?.value || firstMergeInv.unit || modalUnit,
              itemId: firstMergeInv.itemId ?? firstMergeInv.item?.itemId,
            })
          }

          splitMap.set('temp-merged', {
            inventoryId: 'temp-merged',
            arrNew,
            originalInventory: mergedVirtual,
          })

          pieceWeights.forEach((w, pieceIdx) => {
            selectedRows.push({
              src: 'plannedSplit',
              originalInventory: mergedVirtual,
              origInventoryId: 'temp-merged',
              amount: w,
              pcs: 1,
              splitPlanPendingId: 'temp-merged',
              splitArrIndex: pieceIdx,
              pieceIndex: numIntact + pieceIdx,
            })
          })
        } else {
          // No split needed, take the merged virtual inventory whole as the last piece
          selectedRows.push({
            src: 'direct',
            originalInventory: mergedVirtual,
            origInventoryId: 'temp-merged',
            amount: mergeSum,
            pcs: 1,
            pieceIndex: p - 1,
          })
        }
      }

      // Convert splitMap to splitPlansLocal
      const splitPlansLocal = []
      const splitList = Array.from(splitMap.values())
      
      splitList.forEach((s) => {
        const originalInv = s.originalInventory
        const usedSum = s.arrNew.reduce((sm, x) => sm + parseNumber(x.amount), 0)
        const origTotal = roundTo(parseNumber(originalInv.amount))
        const remainder = roundTo(origTotal - usedSum)

        if (remainder > TOLERANCE) {
          s.arrNew.push({
            amount: remainder,
            unit: originalInv.unit?.value || originalInv.unit || originalInv.item?.unit || modalUnit,
            itemId: originalInv.itemId ?? originalInv.item?.itemId,
          })
        }

        const idxPlan = splitPlansLocal.length
        splitPlansLocal.push({
          inventoryId: s.inventoryId,
          arrNew: s.arrNew,
          originalInventory: s.originalInventory,
        })

        // Update selectedRows references
        selectedRows.forEach((row) => {
          if (row.splitPlanPendingId === s.inventoryId) {
            row.splitPlanIndex = idxPlan
          }
        })
      })

      // Clean up temp pending fields
      selectedRows.forEach((row) => {
        delete row.splitPlanPendingId
      })

      const usedInventoryIds = new Set(
        selectedRows
          .map(
            (r) =>
              r.origInventoryId ??
              r.originalInventory?.id ??
              r.inventoryId ??
              r.id ??
              null,
          )
          .filter(Boolean),
      )
      setModalUsedInventoryCount(usedInventoryIds.size)

      // Transform selected rows
      const transformRow = (e) => ({
        itemId: e?.itemId ?? e?.originalInventory?.itemId,
        SKU:
          e?.sku ??
          e?.SKU ??
          e?.item?.SKU ??
          e?.item?.sku ??
          e?.originalInventory?.SKU ??
          e?.originalInventory?.sku ??
          e?.originalInventory?.item?.SKU ??
          e?.originalInventory?.item?.sku ??
          undefined,
        amount: e?.amount ?? e?.item?.amount ?? e?.amount,
        pcs: Number(e?.pcs ?? e?.item?.pcs ?? 1),
        imageUrl:
          e?.img ??
          e?.item?.linkFoto ??
          e?.originalInventory?.img ??
          e?.imageUrl ??
          '',
        nameItem:
          e?.nameItem ?? e?.item?.nameItem ?? e?.originalInventory?.nameItem,
        warehouse:
          e?.warehouse ??
          e?.item?.warehouse ??
          e?.originalInventory?.warehouse ??
          '',
        warehouseName:
          e?.warehouseName ??
          e?.item?.warehouseName ??
          e?.originalInventory?.warehouseName ??
          '',
        unit: autoFetchPublicVersion?.unitArray?.find(
          (z) =>
            (z?.value ?? '').toLowerCase() ===
            (
              e?.unit ??
              e?.item?.unit ??
              e?.originalInventory?.unit ??
              ''
            ).toLowerCase(),
        ),
        inventoryId:
          e?.id ??
          e?.inventoryId ??
          e?.origInventoryId ??
          e?.originalInventory?.id ??
          null,
        packingId: e?.groupId,
        fieldNum: fieldNum,
        pieceIndex: e?.pieceIndex,
        ...(e?.splitPlanIndex !== undefined && {
          splitPlanIndex: e.splitPlanIndex,
        }),
        ...(e?.splitArrIndex !== undefined && {
          splitArrIndex: e.splitArrIndex,
        }),
        ...(e?.origInventoryId !== undefined && {
          origInventoryId: e.origInventoryId,
        }),
        ...(e?.originalInventory?.lotCode !== undefined && {
          lotCode: e.originalInventory.lotCode,
        }),
        ...(e?.originalInventory?.lot !== undefined && {
          lotCode: e.originalInventory.lot,
        }),
        ...(e?.lotCode !== undefined && { lotCode: e.lotCode }),
        ...(e?.originalInventory?.ownerCode !== undefined && {
          ownerCode: e.originalInventory.ownerCode,
        }),
        ...(e?.ownerCode !== undefined && { ownerCode: e.ownerCode }),
        ...(e?.src && { _src: e.src }),
        ...(e?._isFromRemainder && { _isFromRemainder: true }),
      })

      const newData = selectedRows.map(transformRow)
      // set initial, we'll override below with the final grouped modal rows after aggregation
      setModalSelectedRows(newData)

      // Filter by lot ID if specified (normalizedLotId is already computed earlier above)

      const filteredData = shouldFilterByLot
        ? newData.filter(
            (row) =>
              Number(row.packingId) === normalizedLotId ||
              !row.packingId ||
              row._src === 'plannedSplit',
          )
        : newData

      // If we are filtering by lot/packing then update the displayed available counts
      if (shouldFilterByLot) {
        const availableFiltered = (available || []).filter(
          (e) => Number(e.packingId) === normalizedLotId || !e.packingId,
        )
        const availableFilteredKg = (availableFiltered || []).reduce(
          (sum, row) =>
            sum +
            parseNumber(row.amount ?? row.item?.amount ?? row?.item?.size ?? 0),
          0,
        )
        setModalAvailableKg(availableFilteredKg)
        setModalAvailableCount(availableFiltered?.length ?? 0)
      }

      let finalRows = filteredData.map((r, index) => ({
        ...r,
        amount: roundTo(r.amount),
      }))

      // Return exact multiple fragmented pieces so max amount logic correctly limits items
      setModalSelectedRows(finalRows)
      setModalGenRows(finalRows)
      setModalSplitPlans(splitPlansLocal)
      setModalMergePlan(mergePlanLocal)
      setModalGenError('')
      setModalHasGenerated(true)
      setLoading(false)
    } catch (error) {
      console.error('Error while generating in modal:', error)
      setModalGenError('Terjadi kesalahan saat memproses Auto-pick.')
      setModalHasGenerated(true)
      setLoading(false)
    }
  }

  // Apply auto-pick (execute splits & merges)
  const handleApplyAutoPick = async () => {
    const sourceRows = modalSelectedRows
    if (!sourceRows || sourceRows.length === 0) return
    setLoading(true)
    let rowsAfterSplit = [...sourceRows]
    let currentSplitPlans = modalSplitPlans ? [...modalSplitPlans] : []

    let realMergedId = null

    // 1. Process merge plan if any
    if (modalHasGenerated && modalMergePlan) {
      try {
        const mergePayload = {
          arrInventoryIds: modalMergePlan.arrInventoryIds,
          newAmount: Number(modalMergePlan.newAmount),
          unit: modalMergePlan.unit,
          newItemId: modalMergePlan.newItemId,
          ownerCode: modalMergePlan.ownerCode,
          ...(modalMergePlan?.bundle && {
            bundle: Number(modalMergePlan.bundle),
          }),
        }

        const res = await dispatch(mergeInventory(mergePayload)).unwrap()
        const mergedInv = res?.data ?? res?.item ?? res?.result ?? res
        realMergedId = mergedInv?.id ?? mergedInv

        if (!realMergedId) {
          throw new Error('Gagal mendapatkan ID inventori hasil penggabungan.')
        }

        // Replace 'temp-merged' in rowsAfterSplit with the real merged inventory ID and details
        rowsAfterSplit = rowsAfterSplit.map((row) => {
          if (row.origInventoryId === 'temp-merged') {
            return {
              ...row,
              origInventoryId: realMergedId,
              inventoryId: row.inventoryId === 'temp-merged' ? realMergedId : row.inventoryId,
              id: row.id === 'temp-merged' ? realMergedId : row.id,
              SKU:
                mergedInv?.SKU ??
                mergedInv?.sku ??
                mergedInv?.item?.SKU ??
                mergedInv?.item?.sku ??
                row.SKU,
              lotCode:
                mergedInv?.lotCode ??
                mergedInv?.lot ??
                mergedInv?.lotId ??
                mergedInv?.item?.lotCode ??
                row.lotCode,
              nameItem:
                mergedInv?.nameItem ??
                mergedInv?.itemName ??
                mergedInv?.item?.nameItem ??
                row.nameItem,
              ownerCode:
                mergedInv?.ownerCode ??
                mergedInv?.ownerCode ??
                row.ownerCode,
            }
          }
          return row
        })

        // Replace 'temp-merged' in split plans with the real merged inventory ID
        currentSplitPlans = currentSplitPlans.map((plan) => {
          if (plan.inventoryId === 'temp-merged') {
            return {
              ...plan,
              inventoryId: realMergedId,
            }
          }
          return plan
        })
      } catch (error) {
        console.error('Error during mergeInventory:', error)
        setModalGenError('Terjadi kesalahan saat menggabungkan inventori.')
        setLoading(false)
        return
      }
    }

    // 2. Process split plans - sekarang SATU split plan per inventory
    if (modalHasGenerated && currentSplitPlans.length > 0) {
      try {
        for (let i = 0; i < currentSplitPlans.length; i++) {
          const plan = currentSplitPlans[i]
          if (!plan || !plan.inventoryId || !Array.isArray(plan.arrNew))
            continue

          const payload = {
            inventoryId: plan.inventoryId,
            arrNew: plan.arrNew.map((a) => ({
              amount: parseNumber(a.amount),
              unit: a.unit?.value || a.unit || modalUnit,
              itemId: a.itemId,
              ...((a?.bundle || plan?.bundle) && {
                bundle: Number(a?.bundle || plan?.bundle),
              }),
            })),
            notes: `Auto-pick-split-${plan.inventoryId}-${Date.now()}`,
          }

          const res = await dispatch(splitToManyInventory(payload)).unwrap()
          const inventoriesResult = Array.isArray(res)
            ? res
            : res?.items ||
              res?.data ||
              res?.newInventories ||
              res?.result ||
              []
          if (
            !Array.isArray(inventoriesResult) ||
            inventoriesResult.length === 0
          )
            continue

          // Hasil split sudah terurut sesuai arrNew
          // Update rows berdasarkan splitArrIndex karena itu adalah index di arrNew
          for (let ri = 0; ri < rowsAfterSplit.length; ri++) {
            const row = rowsAfterSplit[ri]
            if (row.splitPlanIndex !== i) continue
            // Gunakan splitArrIndex sebagai index ke hasil split
            const splitIdx = row.splitArrIndex
            if (splitIdx !== undefined && splitIdx < inventoriesResult.length) {
              const newInv = inventoriesResult[splitIdx]
              rowsAfterSplit[ri] = {
                ...row,
                inventoryId: newInv.id,
                id: newInv.id,
                SKU:
                  newInv.SKU ??
                  newInv.sku ??
                  newInv.Sku ??
                  newInv.item?.SKU ??
                  newInv.item?.sku ??
                  row.SKU ??
                  row.sku,
                lotCode:
                  newInv.lotCode ??
                  newInv.lot ??
                  newInv.lotId ??
                  newInv.item?.lotCode ??
                  row.lotCode,
                nameItem:
                  newInv.nameItem ??
                  newInv.itemName ??
                  newInv.item?.nameItem ??
                  row.nameItem,
                amount: roundTo(newInv.amount ?? row.amount),
              }
            }
          }
        }
      } catch (error) {
        console.error('Error during splitToManyInventory:', error)
        setModalGenError('Terjadi kesalahan saat melakukan split inventori.')
        setLoading(false)
        return
      }
    }

    if (onApply && rowsAfterSplit.length > 0) {
      const preparedRows = rowsAfterSplit.map((r) => ({
        ...r,
        amount: roundTo(r.amount),
        SKU:
          r.SKU ??
          r.sku ??
          r.item?.SKU ??
          r.item?.sku ??
          r.inventory?.item?.SKU ??
          undefined,
      }))
      onApply({
        finalRows: preparedRows,
        parseNumber,
      })
    }

    // Close modal
    toggle()
    resetModalStates()
    setLoading(false)
  }

  // Effect: Initialize on open - gunakan flag untuk track sudah diinisialisasi
  const [hasInitialized, setHasInitialized] = useState(false)

  if (isOpen && !hasInitialized) {
    initializeModal()
    setHasInitialized(true)
  }

  // Reset flag ketika modal ditutup
  if (!isOpen && hasInitialized) {
    setHasInitialized(false)
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      onClosed={resetModalStates}
      size="lg"
      centered
      data-testid="sell-form-modal-autopick"
    >
      <ModalHeader toggle={toggle}>
        <span className="fw-bold">Pratinjau Pilihan Otomatis</span>
      </ModalHeader>
      <ModalBody>
        <h5 className="mb-3 fw-bold">{orderItem?.nameItem}</h5>

        {/* Input Section */}
        {!modalHasGenerated ? (
          <div className="mb-3">
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="modalNeededKg">Berat ({modalUnit})</Label>
                  <InputGroup>
                    <AmountInput
                      id="modalNeededKg"
                      name="modalNeededKg"
                      value={modalNeededKg}
                      cb={(val) => setModalNeededKg(val)}
                      placeholder={`Contoh: 100`}
                    />
                    <InputGroupText>{modalUnit}</InputGroupText>
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="modalPcs">Jumlah (pcs)</Label>
                  <PMBInputNumber
                    id="modalPcs"
                    type="text"
                    min={1}
                    value={modalPcs}
                    onChange={(e) => setModalPcs(e.target.value)}
                    placeholder="Contoh: 10"
                  />
                </FormGroup>
              </Col>
            </Row>
            <div className="text-end mt-2">
              <CustomButton
                color="primary"
                onClick={generateAutoPickFromModal}
                disabled={
                  loading ||
                  !modalNeededKg ||
                  Number(modalNeededKg) <= 0 ||
                  !(orderItem?.itemId || orderItem?.id)
                }
              >
                {loading ? 'Memproses...' : 'Lihat Hasil'}
              </CustomButton>
            </div>
          </div>
        ) : (
          /* Summary Section */
          <div
            className="p-3 mb-3 bg-light rounded"
            style={{ borderLeft: '4px solid #0063df' }}
          >
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>📋 Ringkasan Picking</strong>
              <span className="text-muted" style={{ fontSize: 12 }}>
                Terpakai {modalUsedInventoryCount || 0} dari{' '}
                {modalAvailableCount || 0} item
              </span>
            </div>
            <Row className="g-2">
              <Col>
                <div className="p-2 bg-white rounded border">
                  <small className="text-muted d-block mb-1">Tersedia</small>
                  <div className="fw-bold">
                    {intl.formatNumber(parseNumber(modalAvailableKg), {
                      useGrouping: 'always',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    })}{' '}
                    {modalUnit}
                  </div>
                </div>
              </Col>
              <Col>
                <div className="p-2 bg-white rounded border">
                  <small className="text-muted d-block mb-1">Diminta</small>
                  <div className="fw-bold">
                    {intl.formatNumber(parseNumber(modalNeededKg) || 0, {
                      useGrouping: 'always',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    })}{' '}
                    {modalUnit}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        )}

        {/* Results Table */}
        {modalHasGenerated ? (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>📦 Rincian Item</strong>
              <CustomButton
                color="secondary"
                outline
                size="sm"
                onClick={() => {
                  setModalHasGenerated(false)
                  setModalSplitPlans([])
                }}
              >
                Ubah input
              </CustomButton>
            </div>

            {modalGenError ? (
              <div
                className="alert alert-danger d-flex align-items-center p-2 mb-3"
                role="alert"
              >
                <span className="me-2">⚠️</span>
                <div>{modalGenError}</div>
              </div>
            ) : (
              <div style={{ maxHeight: '45vh', overflowY: 'auto' }}>
                <table className="table table-striped table-bordered table-hover table-sm mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th className="text-center" style={{ width: '60px' }}>
                        No
                      </th>
                      <th className="text-end">Berat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      if (!modalGenRows || modalGenRows.length === 0) {
                        return (
                          <tr>
                            <td
                              colSpan={2}
                              className="text-center text-muted py-4"
                            >
                              Belum ada detail hasil picking.
                            </td>
                          </tr>
                        )
                      }

                      const displayRows = []
                      const grouped = {}
                      modalGenRows.forEach((r) => {
                        if (!grouped[r.pieceIndex]) grouped[r.pieceIndex] = []
                        grouped[r.pieceIndex].push(r)
                      })

                      Object.values(grouped).forEach((group) => {
                        const first = group[0]
                        const totalAmount = group.reduce(
                          (sum, item) => sum + parseNumber(item.amount),
                          0,
                        )
                        const hasSplit = group.some(
                          (item) => item.splitPlanIndex !== undefined,
                        )
                        displayRows.push({
                          ...first,
                          amount: totalAmount,
                          isGrouped: group.length > 1,
                          groupCount: group.length,
                          hasSplit,
                        })
                      })

                      return displayRows.map((row, i) => (
                        <tr key={`gen-row-${i}`}>
                          <td className="text-center">{i + 1}</td>
                          <td>
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="d-flex flex-column">
                                <small className="text-muted">
                                  {row.nameItem}
                                </small>
                                <small className="text-muted">
                                  Kode Inventori:{' '}
                                  {row.originalInventory?.ownerCode ||
                                    row.ownerCode ||
                                    '-'}
                                  {'  '}
                                </small>
                                <small className="text-muted">
                                  Lot ID:{' '}
                                  {row.originalInventory?.lots &&
                                  Array.isArray(row.originalInventory.lots)
                                    ? row.originalInventory.lots
                                        .map(
                                          (lot) =>
                                            lot.lotCode || lot.lot || lot,
                                        )
                                        .join(', ')
                                    : row.lotCode || '-'}
                                </small>
                                {(row.warehouse || row.warehouseName) && (
                                  <small className="text-muted">
                                    {row.warehouseName || row.warehouse || ''}
                                  </small>
                                )}
                              </div>
                              <span className="fw-bold text-end ms-3">
                                {intl.formatNumber(parseNumber(row.amount), {
                                  useGrouping: 'always',
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 2,
                                })}{' '}
                                {row.unit?.value || modalUnit}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))
                    })()}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <div className="text-center text-muted p-3 bg-light rounded">
            Masukkan berat dan jumlah lalu klik "Lihat Hasil"
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <CustomButton color="secondary" outline onClick={toggle} testId="sell-form-modal-autopick-close">
          Batal
        </CustomButton>
        <CustomButton
          color="primary"
          onClick={handleApplyAutoPick}
          disabled={!modalHasGenerated || modalGenRows?.length === 0 || loading}
          testId="sell-form-modal-autopick-submit"
        >
          {loading ? 'Memproses...' : '✓ Terapkan'}
        </CustomButton>
      </ModalFooter>
    </Modal>
  )
}

export default ModalAutoPick
